import { useState } from 'react';
import {
  useBookingLinks,
  useCreateBookingLink,
  useUpdateBookingLink,
  useDeleteBookingLink,
  type BookingLink,
} from '../../api/booking-links.api';
import { useToast } from '../../components/ui/Toast';

/* Settings · Booking links — Sprint 16a.
   The recruiter manages their own set of public booking page URLs. The
   default is what `{{calendar_url}}` resolves to at send time; in the
   builder, the `calendar_slot` block picks any link by label. */

export function BookingLinksSection() {
  const { data, isLoading } = useBookingLinks();
  const links = data?.data ?? [];

  return (
    <section style={shell()}>
      <header style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--color-text-brand, #1e1b4b)' }}>
          Booking links
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-muted, #6b6b7d)' }}>
          Public calendar URLs (e.g. <em>15 min intro</em>, <em>30 min deep dive</em>). Templates' "Find a time"
          buttons pick from this list. The default link resolves to <code style={code()}>{'{{calendar_url}}'}</code>{' '}
          at send time.
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-muted, #6b6b7d)' }}>
          Booking links you set up in JobTetris (Account, Scheduling) appear here automatically within about
          30 minutes. Change or remove those in JobTetris. You can also add links from other calendars below.
        </p>
      </header>

      <NewLinkForm />

      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
      ) : links.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No booking links yet. Add your first above.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {links.map((l) => (
            <LinkRow key={l.id} link={l} />
          ))}
        </ul>
      )}
    </section>
  );
}

function NewLinkForm() {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const create = useCreateBookingLink();
  const { addToast } = useToast();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !url.trim()) return;
    try {
      await create.mutateAsync({ label: label.trim(), url: url.trim() });
      setLabel('');
      setUrl('');
      addToast({ variant: 'success', title: 'Booking link added' });
    } catch {
      addToast({ variant: 'danger', title: 'Add failed' });
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-end',
        marginBottom: 16,
        padding: 12,
        background: 'var(--color-surface-muted, #f7f8fc)',
        borderRadius: 6,
      }}
    >
      <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={lbl()}>Label</span>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="15 min intro"
          style={input()}
          data-testid="bl-new-label"
        />
      </label>
      <label style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={lbl()}>URL</span>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://cal.com/me/15min"
          style={input()}
          data-testid="bl-new-url"
        />
      </label>
      <button
        type="submit"
        disabled={!label.trim() || !url.trim() || create.isPending}
        style={btn(create.isPending)}
        data-testid="bl-new-submit"
      >
        {create.isPending ? '…' : 'Add'}
      </button>
    </form>
  );
}

function LinkRow({ link }: { link: BookingLink }) {
  const update = useUpdateBookingLink(link.id);
  const del = useDeleteBookingLink();
  const { addToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(link.label);
  const [draftUrl, setDraftUrl] = useState(link.url);

  const setDefault = async () => {
    if (link.is_default) return;
    await update.mutateAsync({ is_default: true });
  };

  const save = async () => {
    try {
      await update.mutateAsync({ label: draftLabel.trim(), url: draftUrl.trim() });
      setEditing(false);
    } catch {
      addToast({ variant: 'danger', title: 'Save failed' });
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete booking link "${link.label}"?`)) return;
    try {
      await del.mutateAsync(link.id);
    } catch {
      addToast({ variant: 'danger', title: 'Delete failed' });
    }
  };

  return (
    <li
      data-testid={`bl-row-${link.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        border: '1px solid var(--color-border-default, #ececf2)',
        borderRadius: 6,
        background: '#fff',
      }}
    >
      {editing ? (
        <>
          <input value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)} style={{ ...input(), flex: 1 }} />
          <input value={draftUrl} onChange={(e) => setDraftUrl(e.target.value)} style={{ ...input(), flex: 2 }} />
          <button type="button" onClick={save} style={btn(false)}>Save</button>
          <button type="button" onClick={() => { setEditing(false); setDraftLabel(link.label); setDraftUrl(link.url); }} style={ghost()}>
            Cancel
          </button>
        </>
      ) : (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-brand)', display: 'flex', gap: 8, alignItems: 'center' }}>
              {link.label}
              {link.is_default && (
                <span data-testid={`bl-default-${link.id}`} style={badge()}>default</span>
              )}
              {link.jt_event_type_id && (
                <span data-testid={`bl-from-jt-${link.id}`} style={badge()}>from JobTetris</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono, ui-monospace, monospace)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {link.url}
            </div>
          </div>
          {!link.is_default && (
            <button type="button" onClick={setDefault} data-testid={`bl-make-default-${link.id}`} style={ghost()}>
              Make default
            </button>
          )}
          {/* Copied links follow JobTetris (JT1-1919), so they're edited there. */}
          {!link.jt_event_type_id && (
            <>
              <button type="button" onClick={() => setEditing(true)} style={ghost()}>Edit</button>
              <button type="button" onClick={remove} style={{ ...ghost(), color: 'var(--color-danger, #e03131)' }}>Delete</button>
            </>
          )}
        </>
      )}
    </li>
  );
}

/* ── small inline-style helpers ── */
function shell(): React.CSSProperties {
  return {
    background: 'var(--card-bg, #fff)',
    border: '1px solid var(--card-border, #ececf2)',
    borderRadius: 'var(--card-radius, 8px)',
    boxShadow: 'var(--card-shadow, none)',
    padding: 24,
  };
}
function input(): React.CSSProperties {
  return {
    padding: '6px 10px',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
  };
}
function lbl(): React.CSSProperties {
  return {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--color-text-muted, #6b6b7d)',
    textTransform: 'uppercase',
    letterSpacing: 0.04,
  };
}
function btn(busy: boolean): React.CSSProperties {
  return {
    padding: '6px 14px',
    background: 'var(--color-action-primary, #1e1b4b)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };
}
function ghost(): React.CSSProperties {
  return {
    padding: '4px 10px',
    background: '#fff',
    color: 'var(--color-text-secondary, #2b2b3c)',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  };
}
function badge(): React.CSSProperties {
  return {
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 700,
    background: 'var(--color-blue-50, #e8edff)',
    color: 'var(--color-brand-blue, #3b5bdb)',
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: 0.04,
  };
}
function code(): React.CSSProperties {
  return {
    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
    fontSize: 12,
    background: 'var(--color-surface-muted, #f7f8fc)',
    padding: '1px 6px',
    borderRadius: 3,
  };
}
